import * as React from 'react';
import type { ComparisonRowDef, ComparisonTableApi } from '../types';

/** Sticky label column width when no row uses `labelTooltip`. */
export const LABEL_COL_PX = 80;

/** Sticky label column width when any row shows an info-icon tooltip. */
export const LABEL_COL_PX_WITH_TOOLTIP = 140;

export function resolveLabelColPx(
  rowDefs: Pick<ComparisonRowDef, 'labelTooltip'>[],
): number {
  return rowDefs.some((row) => Boolean(row.labelTooltip?.trim()))
    ? LABEL_COL_PX_WITH_TOOLTIP
    : LABEL_COL_PX;
}

interface UseTableScrollApiOptions {
  columnCount: number;
  visibleItems: number;
  labelColPx?: number;
  setApi?: (api: ComparisonTableApi | null) => void;
}

interface UseTableScrollApiResult {
  scrollContainerRef: React.RefObject<HTMLDivElement>;
  headerScrollContainerRef: React.RefObject<HTMLDivElement>;
  imageScrollContainerRef: React.RefObject<HTMLDivElement>;
  colStyle: React.CSSProperties | undefined;
  tableMinWidth: number | undefined;
  currentPage: number;
}

// This hook is responsible for managing the scroll state of the table.
// It is used to determine:
// the width of each column and the current page.
// if the table can scroll prev and next.
// the current page.
// the total pages.
// if the table can scroll prev and next.
// This is because of the necessity of having a table the can behave like a carousel.  Carousel needs slides, but this table could not be done with vertical items to then slide.  It is a horizontal table
export function useTableScrollApi({
  columnCount,
  visibleItems,
  labelColPx = LABEL_COL_PX,
  setApi,
}: UseTableScrollApiOptions): UseTableScrollApiResult {
  const scrollContainerRef = React.useRef<HTMLDivElement>(null);
  const headerScrollContainerRef = React.useRef<HTMLDivElement>(null);
  const imageScrollContainerRef = React.useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = React.useState(0);
  const [canScrollPrev, setCanScrollPrev] = React.useState(false);
  const [canScrollNext, setCanScrollNext] = React.useState(false);
  const [currentPage, setCurrentPage] = React.useState(0);

  React.useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    const observer = new ResizeObserver(([entry]) => {
      setContainerWidth(entry.contentRect.width);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Each data column gets exactly 1/visibleItems of the available space (after the label col).
  // Using pixel values keeps widths honest when the table overflows the container.
  // Clamp visibleItems to [1, columnCount] so we never divide by 0 or produce negative widths.
  const clampedVisibleItems = Math.max(1, Math.min(visibleItems, columnCount));
  // Guard the subtraction so a narrow container never produces a negative dividend.
  const availableWidth = Math.max(0, containerWidth - labelColPx);
  const colPx = containerWidth
    ? Math.floor(availableWidth / clampedVisibleItems)
    : undefined;

  // Ref so scroll callbacks always read the latest colPx without needing it as a dependency.
  const colPxRef = React.useRef<number | undefined>(undefined);
  colPxRef.current = colPx;

  const colStyle: React.CSSProperties | undefined = colPx
    ? { width: colPx, minWidth: colPx, maxWidth: colPx }
    : undefined;

  // Table must be at least wide enough to hold all columns without squishing them.
  const tableMinWidth = colPx ? labelColPx + colPx * columnCount : undefined;

  const showControls = columnCount > clampedVisibleItems;
  const totalPages = showControls ? columnCount - clampedVisibleItems + 1 : 1;

  const updateScrollState = React.useCallback(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    const offset = el.scrollLeft;
    if (headerScrollContainerRef.current) {
      headerScrollContainerRef.current.scrollLeft = offset;
    }
    if (imageScrollContainerRef.current) {
      imageScrollContainerRef.current.scrollLeft = offset;
    }
    setCanScrollPrev(offset > 0);
    setCanScrollNext(offset < el.scrollWidth - el.clientWidth - 1);
    const px = colPxRef.current;
    if (px) {
      setCurrentPage(Math.round(offset / px));
    }
  }, []);

  React.useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    el.addEventListener('scroll', updateScrollState, { passive: true });
    updateScrollState();
    return () => el.removeEventListener('scroll', updateScrollState);
  }, [updateScrollState]);

  // When the user scrolls the sticky header or image row directly (e.g. by
  // clicking and dragging on it), sync the scroll position back to the main
  // body container so everything moves together.
  // A flag prevents the reverse-sync from firing when the body is already
  // driving the scroll (e.g. carousel buttons), which would cause a loop.
  React.useEffect(() => {
    const header = headerScrollContainerRef.current;
    const image = imageScrollContainerRef.current;
    const body = scrollContainerRef.current;
    if (!header || !body) return;

    let bodyDriving = false;

    const onHeaderScroll = () => {
      if (bodyDriving) return;
      body.scrollLeft = header.scrollLeft;
      if (image) image.scrollLeft = header.scrollLeft;
      updateScrollState();
    };
    const onImageScroll = () => {
      if (bodyDriving || !image) return;
      body.scrollLeft = image.scrollLeft;
      header.scrollLeft = image.scrollLeft;
      updateScrollState();
    };
    const onBodyScroll = () => {
      bodyDriving = true;
      // Reset after the current scroll event has propagated
      requestAnimationFrame(() => {
        bodyDriving = false;
      });
    };

    header.addEventListener('scroll', onHeaderScroll, { passive: true });
    image?.addEventListener('scroll', onImageScroll, { passive: true });
    body.addEventListener('scroll', onBodyScroll, { passive: true });
    return () => {
      header.removeEventListener('scroll', onHeaderScroll);
      image?.removeEventListener('scroll', onImageScroll);
      body.removeEventListener('scroll', onBodyScroll);
    };
  }, [updateScrollState]);

  // Re-evaluate scroll bounds after a resize changes containerWidth
  React.useEffect(() => {
    updateScrollState();
  }, [containerWidth, updateScrollState]);

  const scrollPrev = React.useCallback(() => {
    const px = colPxRef.current;
    if (!scrollContainerRef.current || !px) return;
    scrollContainerRef.current.scrollBy({ left: -px, behavior: 'smooth' });
  }, []);

  const scrollNext = React.useCallback(() => {
    const px = colPxRef.current;
    if (!scrollContainerRef.current || !px) return;
    scrollContainerRef.current.scrollBy({ left: px, behavior: 'smooth' });
  }, []);

  const scrollToPage = React.useCallback((index: number) => {
    const px = colPxRef.current;
    if (!scrollContainerRef.current || !px) return;
    scrollContainerRef.current.scrollTo({
      left: index * px,
      behavior: 'smooth',
    });
  }, []);

  React.useEffect(() => {
    if (!setApi) return;
    if (!showControls) {
      setApi(null);
      return () => setApi(null);
    }
    setApi({
      scrollPrev,
      scrollNext,
      scrollToPage,
      canScrollPrev,
      canScrollNext,
      currentPage,
      totalPages,
    });
    return () => setApi(null);
  }, [
    setApi,
    showControls,
    scrollPrev,
    scrollNext,
    scrollToPage,
    canScrollPrev,
    canScrollNext,
    currentPage,
    totalPages,
  ]);

  return {
    scrollContainerRef,
    headerScrollContainerRef,
    imageScrollContainerRef,
    colStyle,
    tableMinWidth,
    currentPage,
  };
}
